import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Detects the current OS/browser preference.
 * Returns 'dark' or 'light'.
 */
export const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

/**
 * Directly applies or removes the 'dark' and 'light' classes on document.documentElement
 * and sets the document's color-scheme.
 */
export const applyThemeToDocument = (themeSetting) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const resolved = themeSetting === "system" ? getSystemTheme() : themeSetting;

  if (resolved === "dark") {
    root.classList.add("dark");
    root.classList.remove("light");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.classList.add("light");
    root.style.colorScheme = "light";
  }

  return resolved;
};

const useThemeLayoutStore = create(
  persist(
    (set, get) => ({
      // Layout state (preserving existing layoutStore compatibility)
      activeTab: "user-Login",
      selectedContact: null,
      setSelectedContact: (contact) => set({ selectedContact: contact }),
      setActiveTab: (tab) => set({ activeTab: tab }),

      // 3-mode theme state: 'light' | 'dark' | 'system'
      theme: "system",
      resolvedTheme: getSystemTheme(),
      effectiveTheme: getSystemTheme(),

      setTheme: (newTheme) => {
        if (!["light", "dark", "system"].includes(newTheme)) return;
        const resolved = applyThemeToDocument(newTheme);
        set({ theme: newTheme, resolvedTheme: resolved, effectiveTheme: resolved });
      },

      initTheme: () => {
        const currentTheme = get().theme || "system";
        const resolved = applyThemeToDocument(currentTheme);
        set({ resolvedTheme: resolved, effectiveTheme: resolved });
      },
    }),
    {
      name: "theme-layout-storage",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default useThemeLayoutStore;
